import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { ApiInterface } from '../api-interface';
import { ApiService } from '../api.service';
import {
  ComissaoAdminFiltroDTO,
  ComissaoAdminListagemDTO,
  ComissaoConsultaDTO,
  ComissaoPagamentosConsultaDTO,
  ComissaoRelatorioPdfFiltroDTO,
  ConsultaIndicacaoMasterDTO,
  ConviteIndicacaoDTO,
  CriarCupomMasterDTO,
  DescontoIndicacaoPendenteConsultaDTO,
  CupomMasterItemDTO,
  EmpresaBuscaMasterDTO,
  IndicacaoConsultaDTO,
  IndicacaoListagemDTO,
  MeuCodigoIndicacaoConsultaDTO,
  MeusCuponsIndicacaoConsultaDTO,
  PageCupomMasterDTO,
  PagamentoComissaoFiltroDTO,
  PagamentoComissaoRelatorioFiltroDTO,
  PagePagamentoComissaoDTO,
  RankingIndicacoesConsultaDTO,
  RepasseLoteItemDTO,
  RepasseLoteResultadoDTO,
  RepasseComissaoPorValorConsultaDTO,
  CancelamentoLoteResultadoDTO,
  ResumoCupomIndicacaoConsultaDTO,
  AlterarPercentualPadraoCupomDTO,
  PercentualPadraoCupomAtualizadoDTO,
  PercentualPadraoCupomDTO,
  RevogacaoCuponsMasterDTO,
  RevogarCuponsMasterDTO,
} from '../../models/indicacoes/indicacao.model';

/**
 * Service do Programa de Indicacoes.
 *
 * Cobre os endpoints REST do backend (ver `API_CONTRACT_indicacoes.md`):
 * - GET   /indicacoes/meu-codigo        (codigo + link de compartilhamento)
 * - GET   /indicacoes                   (listagem paginada + resumo)
 * - GET   /indicacoes/{id}/comissoes    (drill-down das competencias)
 * - POST  /indicacoes/convite           (convite por email)
 * - GET   /comissoes                    (listagem global master + resumo)
 * - GET   /comissoes/pagamentos         (historico paginado dos pagamentos — master)
 * - PATCH /comissoes/{id}/repasse       (marca comissao como PAGA — master)
 * - PATCH /comissoes/{id}/cancelar      (marca comissao como CANCELADA — master)
 *
 * Implementa `ApiInterface` (padrao `AvisoService`). `put`/`delete` nao se
 * aplicam a esta feature; sao no-op coerentes apenas para satisfazer a interface.
 */
@Injectable({ providedIn: 'root' })
export class IndicacaoService implements ApiInterface {
  constructor(private apiService: ApiService) {}

  // ---------------------------------------------------------------------------
  // ApiInterface
  // ---------------------------------------------------------------------------

  /** Listagem paginada + resumo (GET /indicacoes?page&size&sort). */
  get<T>(url: string, data?: any): Observable<IndicacaoListagemDTO> {
    const queryParams = this.apiService.buildQueryParams(data);
    const path = queryParams ? `${url}?${queryParams}` : url;
    return this.apiService.read<IndicacaoListagemDTO>(path);
  }

  /** Convite por email (POST /indicacoes/convite). */
  post<T>(url: string, data: ConviteIndicacaoDTO): Observable<IndicacaoConsultaDTO> {
    return this.apiService.create<IndicacaoConsultaDTO>(url, data);
  }

  /** Nao se aplica a esta feature (sem atualizacao via PUT). No-op coerente. */
  put<T>(_url: string, _data: any): Observable<any> {
    return EMPTY;
  }

  /** Nao se aplica a esta feature (sem exclusao via DELETE). No-op coerente. */
  delete<T>(_url: string): Observable<any> {
    return EMPTY;
  }

  // ---------------------------------------------------------------------------
  // Metodos especificos da feature
  // ---------------------------------------------------------------------------

  /** GET /indicacoes/meu-codigo — codigo + link de compartilhamento. */
  getMeuCodigo(): Observable<MeuCodigoIndicacaoConsultaDTO> {
    return this.apiService.read<MeuCodigoIndicacaoConsultaDTO>('/indicacoes/meu-codigo');
  }

  /**
   * GET /indicacoes/meus-cupons — seletor de cupons ativos da empresa + resumo
   * GERAL inicial. Load inicial da aba "Meus referidos" (visao do usuario).
   */
  getMeusCupons(): Observable<MeusCuponsIndicacaoConsultaDTO> {
    return this.apiService.read<MeusCuponsIndicacaoConsultaDTO>('/indicacoes/meus-cupons');
  }

  /**
   * GET /indicacoes/meu-resumo?cupomId= — resumo por escopo.
   * Sem cupomId (ou undefined) => escopo GERAL; com cupomId => escopo CUPOM.
   * `buildQueryParams` descarta cupomId undefined, entao o GERAL vai sem query.
   */
  getMeuResumo(cupomId?: number): Observable<ResumoCupomIndicacaoConsultaDTO> {
    const queryParams = this.apiService.buildQueryParams({ cupomId } as any);
    const path = queryParams
      ? `/indicacoes/meu-resumo?${queryParams}`
      : '/indicacoes/meu-resumo';
    return this.apiService.read<ResumoCupomIndicacaoConsultaDTO>(path);
  }

  /**
   * GET /indicacoes/meu-ranking — posicao da empresa autenticada no ranking de
   * indicacoes (uma posicao por tile do resumo). Global da empresa: nao varia
   * com o cupom/escopo selecionado.
   */
  getMeuRanking(): Observable<RankingIndicacoesConsultaDTO> {
    return this.apiService.read<RankingIndicacoesConsultaDTO>('/indicacoes/meu-ranking');
  }

  /**
   * GET /indicacoes — listagem paginada + resumo. `cupomId` opcional filtra a
   * pagina E o resumo pelo escopo de um cupom da propria empresa (consulta_escopo_cupom).
   * `buildQueryParams` descarta `cupomId` undefined, entao sem cupom a query
   * permanece identica a atual (aditividade / regressao zero — CA05).
   */
  listar(page: number, size: number, sort: string, cupomId?: number): Observable<IndicacaoListagemDTO> {
    return this.get('/indicacoes', { page, size, sort, cupomId });
  }

  /** POST /indicacoes/convite — envia convite por email. */
  enviarConvite(email: string): Observable<IndicacaoConsultaDTO> {
    const body: ConviteIndicacaoDTO = { email };
    return this.post('/indicacoes/convite', body);
  }

  /**
   * GET /indicacoes/meu-desconto-indicacao — preview do desconto de indicacao
   * (R15) da empresa autenticada. Sem `planoId` retorna o desconto elegivel geral;
   * com `planoId` inclui a trava R14 (ja usado naquele plano) e plano gratuito.
   * `buildQueryParams` descarta `planoId` undefined, entao sem plano vai sem query.
   */
  getMeuDescontoIndicacao(planoId?: number): Observable<DescontoIndicacaoPendenteConsultaDTO> {
    const queryParams = this.apiService.buildQueryParams({ planoId } as any);
    const path = queryParams
      ? `/indicacoes/meu-desconto-indicacao?${queryParams}`
      : '/indicacoes/meu-desconto-indicacao';
    return this.apiService.read<DescontoIndicacaoPendenteConsultaDTO>(path);
  }

  /** GET /indicacoes/{id}/comissoes — drill-down das competencias. */
  listarComissoes(indicacaoId: number): Observable<ComissaoConsultaDTO[]> {
    return this.apiService.read<ComissaoConsultaDTO[]>(`/indicacoes/${indicacaoId}/comissoes`);
  }

  /**
   * PATCH /comissoes/{id}/repasse — registra o repasse da comissao (master-only no
   * backend). Sem `valor` paga o saldo integral (comissao -> PAGA); com `valor`
   * (0 < valor <= saldo) faz pagamento PARCIAL — a comissao continua PENDENTE com o
   * saldo abatido. O backend valida o intervalo e responde 422 amigavel se invalido.
   * Retorna o `ComissaoConsultaDTO` atualizado (ja com o novo `saldo`).
   */
  marcarRepassePago(comissaoId: number, valor?: number): Observable<ComissaoConsultaDTO> {
    const body = valor != null ? { valor } : {};
    return this.apiService.patch<ComissaoConsultaDTO>(`/comissoes/${comissaoId}/repasse`, body);
  }

  /** PATCH /comissoes/{id}/cancelar — marca a comissao como CANCELADA (master-only no backend). */
  cancelarComissao(comissaoId: number): Observable<ComissaoConsultaDTO> {
    return this.apiService.patch<ComissaoConsultaDTO>(`/comissoes/${comissaoId}/cancelar`, {});
  }

  /**
   * PATCH /comissoes/repasse-lote — marca varias comissoes como pagas de uma vez
   * (master-only no backend), com valor por item (itemizado). Cada item quita o
   * saldo integral (valor === saldo) ou registra pagamento PARCIAL (valor < saldo).
   * Lote parcial: itens invalidos (valor fora do intervalo, nao PENDENTE, etc.) nao
   * derrubam os demais — o retorno detalha quitadas/parciais/ignoradas e o total pago.
   */
  marcarRepasseLote(itens: RepasseLoteItemDTO[]): Observable<RepasseLoteResultadoDTO> {
    return this.apiService.patch<RepasseLoteResultadoDTO>('/comissoes/repasse-lote', { itens });
  }

  /**
   * PATCH /comissoes/cancelar-lote — cancela varias comissoes de uma vez (master-only).
   * Espelha o repasse-lote: so cancela PENDENTES; itens ja pagos/cancelados/inexistentes
   * vao para `ignoradas` (com motivo), sem derrubar o lote. Sem efeitos colaterais.
   */
  cancelarEmLote(ids: number[]): Observable<CancelamentoLoteResultadoDTO> {
    return this.apiService.patch<CancelamentoLoteResultadoDTO>('/comissoes/cancelar-lote', { ids });
  }

  /**
   * PATCH /comissoes/repasse-por-valor — paga POR VALOR as comissoes PENDENTES de um
   * indicador (mais antigas primeiro, parcial na ultima alcancada). Troco (valor que
   * excede o total pendente) volta em `valorNaoUtilizado`. Master-only no backend.
   */
  repassarPorValor(
    indicadorId: number,
    valorTotal: number,
  ): Observable<RepasseComissaoPorValorConsultaDTO> {
    return this.apiService.patch<RepasseComissaoPorValorConsultaDTO>(
      '/comissoes/repasse-por-valor',
      { indicadorId, valorTotal },
    );
  }

  /**
   * GET /comissoes — listagem global de comissoes (master-only no backend),
   * paginada, com filtros dinamicos + resumo de totais dos filtros aplicados.
   * Alimenta a aba "Repasse de Comissoes". Ver `API_CONTRACT_repasse_comissoes.md`.
   */
  listarComissoesAdmin(filtros: ComissaoAdminFiltroDTO): Observable<ComissaoAdminListagemDTO> {
    const queryParams = this.apiService.buildQueryParams(filtros as any);
    const path = queryParams ? `/comissoes?${queryParams}` : '/comissoes';
    return this.apiService.read<ComissaoAdminListagemDTO>(path);
  }

  /**
   * GET /comissoes/pagamentos — historico paginado dos EVENTOS de pagamento de comissao
   * (master-only no backend). Envelope `Page` DIRETO: o chamador le `res.content` —
   * NAO `res.comissoes.content` (isso e GET /comissoes, outro endpoint/parser).
   * Resposta JSON => `read` (NUNCA `readText`). `buildQueryParams` descarta as chaves
   * `undefined` e URL-encoda as barras das datas "dd/MM/yyyy".
   * Ver `vendas/docs/api/API_CONTRACT_historico-pagamento-comissoes.md`.
   */
  listarHistoricoPagamentos(
    filtros: PagamentoComissaoFiltroDTO,
  ): Observable<PagePagamentoComissaoDTO> {
    const queryParams = this.apiService.buildQueryParams(filtros as any);
    const path = queryParams ? `/comissoes/pagamentos?${queryParams}` : '/comissoes/pagamentos';
    return this.apiService.read<PagePagamentoComissaoDTO>(path);
  }

  /**
   * GET /comissoes/relatorio-pdf — PDF (Base64) das comissoes conforme os filtros da tela.
   * Master-only no backend. A resposta e `text/plain` (Base64 puro), por isso usamos
   * `readText` (NUNCA `read`, que faria JSON.parse e quebraria). `buildQueryParams`
   * descarta chaves undefined, entao so os filtros preenchidos sao enviados; `page`/`size`
   * nunca vao (nao existem no tipo `ComissaoRelatorioPdfFiltroDTO`).
   * Ver `vendas/docs/api/API_CONTRACT_relatorio-pdf-comissoes.md`.
   */
  gerarRelatorioComissoesPdf(filtros: ComissaoRelatorioPdfFiltroDTO): Observable<string> {
    const queryParams = this.apiService.buildQueryParams(filtros as any);
    const path = queryParams
      ? `/comissoes/relatorio-pdf?${queryParams}`
      : '/comissoes/relatorio-pdf';
    return this.apiService.readText<string>(path);
  }

  /**
   * GET /comissoes/relatorio-export?...&formato=xlsx|csv — baixa as comissoes como arquivo
   * binario (.xlsx/.csv) com os MESMOS filtros do PDF. Corpo binario via `readBlob`; body
   * vazio (blob.size === 0) sinaliza "sem dados" (tratado no componente). Master-only no
   * backend (403). Espelha `ContadorOperarService.gerarLivroDeVendasArquivo`.
   */
  gerarRelatorioComissoesArquivo(
    formato: 'csv' | 'xlsx',
    filtros: ComissaoRelatorioPdfFiltroDTO,
  ): Observable<Blob> {
    const queryParams = this.apiService.buildQueryParams({ ...filtros, formato } as any);
    return this.apiService.readBlob(`/comissoes/relatorio-export?${queryParams}`);
  }

  /**
   * GET /comissoes/pagamentos/relatorio-pdf — PDF (Base64) do historico de pagamentos
   * conforme os filtros da tela. Master-only no backend (403). Resposta `text/plain`
   * (Base64 puro) => `readText` (NUNCA `read`, que faria JSON.parse e quebraria o Base64).
   * `buildQueryParams` descarta chaves undefined; `page`/`size` nunca vao (nao existem em
   * PagamentoComissaoRelatorioFiltroDTO). Espelha `gerarRelatorioComissoesPdf`.
   * Ver `vendas/docs/api/API_CONTRACT_comissoes-export-historico-pagamentos.md`.
   */
  gerarRelatorioPagamentosPdf(
    filtros: PagamentoComissaoRelatorioFiltroDTO,
  ): Observable<string> {
    const queryParams = this.apiService.buildQueryParams(filtros as any);
    const path = queryParams
      ? `/comissoes/pagamentos/relatorio-pdf?${queryParams}`
      : '/comissoes/pagamentos/relatorio-pdf';
    return this.apiService.readText<string>(path);
  }

  /**
   * GET /comissoes/pagamentos/relatorio-export?...&formato=xlsx|csv — baixa o historico de
   * pagamentos como arquivo binario (.xlsx/.csv) com os MESMOS filtros do PDF. Corpo binario
   * via `readBlob`; body vazio (blob.size === 0) sinaliza "sem dados" (tratado no componente).
   * Master-only no backend (403). Espelha `gerarRelatorioComissoesArquivo`.
   */
  gerarRelatorioPagamentosArquivo(
    formato: 'csv' | 'xlsx',
    filtros: PagamentoComissaoRelatorioFiltroDTO,
  ): Observable<Blob> {
    const queryParams = this.apiService.buildQueryParams({ ...filtros, formato } as any);
    return this.apiService.readBlob(`/comissoes/pagamentos/relatorio-export?${queryParams}`);
  }

  /**
   * GET /comissoes/pagamentos/{id}/comprovante-pdf — comprovante (recibo) em PDF (Base64) de UM
   * evento de pagamento de comissao (PagamentoComissao). Master-only no backend. Espelha
   * `gerarRelatorioComissoesPdf`: a resposta e `text/plain` (Base64 puro), por isso usamos
   * `readText` (NUNCA `read`, que faria JSON.parse e quebraria o Base64). Sem query params — o
   * recurso e identificado apenas pelo `{id}` do path.
   * Ver `vendas/docs/api/API_CONTRACT_comprovante-pagamento-comissao.md`.
   */
  gerarComprovantePagamentoPdf(pagamentoComissaoId: number): Observable<string> {
    return this.apiService.readText<string>(
      `/comissoes/pagamentos/${pagamentoComissaoId}/comprovante-pdf`,
    );
  }

  /**
   * GET /comissoes/{id}/comprovantes-pdf — comprovante CONSOLIDADO em PDF (Base64) de UMA
   * comissao: UM unico documento com o cabecalho da comissao + uma tabela com uma linha por
   * evento de pagamento + rodape de totais (substitui o antigo multi-download, N recibos por
   * evento). Master-only no backend. `{id}` = comissaoId (NAO pagamentoComissaoId). Espelha
   * `gerarComprovantePagamentoPdf`: a resposta e `text/plain` (Base64 puro), por isso usamos
   * `readText` (NUNCA `read`, que faria JSON.parse e quebraria o Base64). Sem query params — o
   * recurso e identificado apenas pelo `{id}` do path. 422 = comissao sem pagamentos / CANCELADA.
   * Ver `vendas/docs/api/API_CONTRACT_comprovante-comissao-consolidado.md`.
   */
  gerarComprovanteConsolidadoComissao(comissaoId: number): Observable<string> {
    return this.apiService.readText<string>(`/comissoes/${comissaoId}/comprovantes-pdf`);
  }

  /**
   * GET /comissoes/{id}/pagamentos — historico lazy dos eventos de pagamento de UMA
   * comissao (cabecalho + lista ordenada). Master-only no backend. A resposta e JSON
   * (valores crus), por isso usamos `read` (NAO `readText`). Sem query params — o recurso
   * e identificado apenas pelo `{id}` do path. Cada evento traz o `pagamentoComissaoId`
   * (aditivo) usado para baixar o comprovante daquele pagamento.
   * Ver `vendas/docs/api/API_CONTRACT_colunas-comissoes-detalhe-pagamentos.md`.
   */
  listarPagamentosComissao(comissaoId: number): Observable<ComissaoPagamentosConsultaDTO> {
    return this.apiService.read<ComissaoPagamentosConsultaDTO>(
      `/comissoes/${comissaoId}/pagamentos`,
    );
  }

  // ---------------------------------------------------------------------------
  // Consulta master — indicacoes de OUTRA empresa (master-only no backend)
  // ---------------------------------------------------------------------------

  /**
   * GET /indicacoes/master/consulta — consulta as indicacoes de OUTRA empresa,
   * por `cupom` OU `empresaId` (informe apenas um). Retorna codigo/link, resumo
   * e a pagina de referidos. `buildQueryParams` descarta chaves undefined, entao
   * so o parametro efetivamente preenchido e enviado.
   */
  consultarMaster(params: {
    cupom?: string;
    empresaId?: number;
    /** consulta_escopo_cupom: restringe ao cupom da empresa-alvo (precede `cupom`). */
    cupomId?: number;
    page: number;
    size: number;
    sort?: string;
  }): Observable<ConsultaIndicacaoMasterDTO> {
    const queryParams = this.apiService.buildQueryParams(params as any);
    const path = queryParams
      ? `/indicacoes/master/consulta?${queryParams}`
      : '/indicacoes/master/consulta';
    return this.apiService.read<ConsultaIndicacaoMasterDTO>(path);
  }

  /**
   * GET /indicacoes/master/cupons — listagem paginada de TODOS os cupons de
   * indicacao (todas as empresas), com codigo/link e resumo. Master-only.
   */
  listarCuponsMaster(
    page: number,
    size: number,
    busca?: string,
    sort?: string,
  ): Observable<PageCupomMasterDTO> {
    // `busca` opcional filtra por codigo do cupom ou empresa; `sort` (formato
    // Spring `campo,asc|desc`) ordena por codigo/porcentagemDesconto/
    // porcentagemComissao (allow-list no backend). buildQueryParams descarta
    // chaves undefined, entao ambos so vao quando preenchidos.
    const queryParams = this.apiService.buildQueryParams({ page, size, busca, sort } as any);
    const path = queryParams
      ? `/indicacoes/master/cupons?${queryParams}`
      : '/indicacoes/master/cupons';
    return this.apiService.read<PageCupomMasterDTO>(path);
  }

  /** POST /indicacoes/master/cupons — cria um cupom de desconto (master-only). */
  criarCupomMaster(dto: CriarCupomMasterDTO): Observable<CupomMasterItemDTO> {
    return this.apiService.create<CupomMasterItemDTO>('/indicacoes/master/cupons', dto);
  }

  /**
   * PATCH /indicacoes/master/cupons/revogar — encerra a validade dos cupons selecionados
   * (passam a valer até agora). Master-only; idempotente para cupons já vencidos.
   */
  revogarCuponsMaster(dto: RevogarCuponsMasterDTO): Observable<RevogacaoCuponsMasterDTO> {
    return this.apiService.patch<RevogacaoCuponsMasterDTO>(
      '/indicacoes/master/cupons/revogar',
      dto,
    );
  }

  /**
   * GET /indicacoes/master/cupons/padrao — percentuais padrão do cupom automático
   * criado para cada empresa. Master-only.
   */
  consultarPercentualPadraoCupom(): Observable<PercentualPadraoCupomDTO> {
    return this.apiService.read<PercentualPadraoCupomDTO>('/indicacoes/master/cupons/padrao');
  }

  /**
   * PUT /indicacoes/master/cupons/padrao — altera os percentuais padrão e os aplica aos
   * cupons automáticos que ainda seguiam o padrão anterior, renomeando o código quando o
   * desconto muda. Master-only.
   */
  alterarPercentualPadraoCupom(
    dto: AlterarPercentualPadraoCupomDTO,
  ): Observable<PercentualPadraoCupomAtualizadoDTO> {
    return this.apiService.update<PercentualPadraoCupomAtualizadoDTO>(
      '/indicacoes/master/cupons/padrao',
      dto,
    );
  }

  /** GET /indicacoes/master/empresas?busca= — autocomplete de empresas (max 20). */
  buscarEmpresasMaster(busca: string): Observable<EmpresaBuscaMasterDTO[]> {
    const queryParams = this.apiService.buildQueryParams({ busca } as any);
    return this.apiService.read<EmpresaBuscaMasterDTO[]>(
      `/indicacoes/master/empresas?${queryParams}`,
    );
  }

  /** GET /indicacoes/master/comissoes/{id} — drill-down master das competencias. */
  listarComissoesMaster(indicacaoId: number): Observable<ComissaoConsultaDTO[]> {
    return this.apiService.read<ComissaoConsultaDTO[]>(
      `/indicacoes/master/comissoes/${indicacaoId}`,
    );
  }
}
